import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  Text,
  IconButton,
  Spacer,
  HStack,
  VStack,
  Button,
  Flex,
} from '@chakra-ui/react';
import {
  CloseCircleOutline,
  formatCurrency,
  Individual,
  individualFullName,
  YellowWarningBold,
} from '@aslanpay/portal-common-lib';

import { useMemo } from 'react';
import getPointsValue from 'app/utils/points';

import { ApproveRecognitionModalProps } from '../RecognitionApprovalsModals.types';

function ApproveRecognitionModal({
  isOpen,
  onClose,
  onConfirm,
  pendingItems,
  isEscalation = false,
}: ApproveRecognitionModalProps) {
  const isBulkAction = pendingItems.length > 1;

  const displayWarning = isEscalation || isBulkAction;

  // TODO ASD-3668: ensure the pot name is rendered correctly (and the rest of the content)
  const renderContent = useMemo(() => {
    if (isBulkAction) {
      return `You are about to approve these entries. This action cannot be undone.`;
    }

    const item = pendingItems?.[0];

    if (!item) {
      return `You are about to approve this recognition. This action cannot be undone.`;
    }

    const sender = individualFullName({
      firstName: item.senderFirstName,
      lastName: item.senderLastName,
    } as Individual);
    const receiver = individualFullName({
      firstName: item.receiverFirstName,
      lastName: item.receiverLastName,
    } as Individual);
    const potName = item.sourcePotName;
    const hasPoints = item.points != null;

    return `If you approve this, ${sender} will recognise ${receiver}${
      hasPoints
        ? ` with ${item.points} points (worth ${formatCurrency(getPointsValue(item.points ?? 0))})${
            potName
              ? `. This amount will be deducted from the ${potName}`
              : '. This amount will be funded from the company wallet'
          }`
        : ''
    }.`;
  }, [pendingItems, isBulkAction]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent sx={{ maxWidth: '592px !important' }}>
        <ModalHeader pb={4}>
          <HStack align="flex-start">
            <Flex direction="row" align="center" gap={2}>
              {displayWarning && <YellowWarningBold boxSize="24px" />}
              <Text as="h4" textStyle="h4Bold" color="brand.black">
                {isBulkAction
                  ? `Bulk approve the ${pendingItems.length} entries selected?`
                  : `Approve ${isEscalation ? 'escalation' : 'recognition'}?`}
              </Text>
            </Flex>

            <Spacer />
            <IconButton
              variant="link"
              icon={<CloseCircleOutline boxSize="30px" />}
              aria-label="Close modal"
              onClick={onClose}
            />
          </HStack>
        </ModalHeader>
        <ModalBody pb={6}>
          <VStack spacing={6} align="stretch">
            <Text>{renderContent}</Text>

            <HStack spacing={3} justify="flex-end">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={onConfirm}>Approve</Button>
            </HStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

export default ApproveRecognitionModal;
